/**
 * Definicje formularzy wniosków EBI (katalog: docs/magistrat/ebi-wnioski). Formularz jest DANYMI — sekcje, pola, warunki
 * widoczności/wymagalności i lista załączników — a jeden renderer (EbiSubmissionForm) rysuje wszystkie wnioski tak samo.
 * Definicje są serializowalne (bez funkcji), więc w kolejnym kroku mogą przyjść z BE i być dostosowywane per urząd.
 *
 * Etykiety, podpowiedzi i oświadczenia to treść urzędowego wzoru wniosku (tylko po polsku, jak sam wzór) — nie teksty
 * interfejsu; interfejs portalu (przyciski, komunikaty, błędy walidacji) idzie przez i18n.
 */

/** Wartości formularza: tekst/data/liczba jako tekst, pola wyboru jako boolean, wielokrotny wybór, pozycje list. */
export type EbiRepeatItem = Record<string, string>;
export type EbiValue = string | boolean | string[] | EbiRepeatItem[];
export type EbiValues = Record<string, EbiValue | undefined>;

/** Warunek nad wartościami pól najwyższego poziomu. */
export type EbiCondition =
  | { field: string; equals: string | boolean }
  | { field: string; in: string[] }
  /** wielokrotny wybór zawiera opcję */
  | { field: string; includes: string }
  | { field: string; filled: true }
  | { all: EbiCondition[] }
  | { any: EbiCondition[] }
  | { not: EbiCondition };

export type EbiFieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "email"
  | "phone"
  | "pesel"
  | "nip"
  | "postalCode"
  | "select"
  | "radio"
  | "checkboxes"
  | "checkbox"
  /** pole wyboru z treścią oświadczenia; wymagane = musi być zaznaczone */
  | "declaration"
  /** tekst informacyjny (np. o opłacie) — nie trafia do danych wniosku */
  | "info"
  /** wyliczona opłata: stawka × liczba z innego pola (albo kwota stała) */
  | "fee"
  /** lista powtarzalnych pozycji (działki, drzewa) */
  | "repeat";

export interface IEbiFieldOption {
  value: string;
  label: string;
}

export interface IEbiField {
  /** Kod pola — klucz wartości i atrybut `Kod` w XML; unikalny w formularzu (pozycje list — w obrębie listy). */
  code: string;
  type: EbiFieldType;
  /** Etykieta pola, a dla „declaration”/„info” — pełna treść. */
  label: string;
  hint?: string;
  placeholder?: string;
  required?: boolean;
  requiredWhen?: EbiCondition;
  visibleWhen?: EbiCondition;
  /** Szerokość w siatce sekcji (domyślnie cała). */
  width?: "full" | "half" | "third" | "twoThirds";
  maxLength?: number;
  /** „number”: zakres, „repeat”: liczba pozycji. */
  min?: number;
  max?: number;
  /** „number”: liczba miejsc po przecinku (kwoty, powierzchnie); domyślnie liczba całkowita. */
  decimals?: number;
  readOnly?: boolean;
  /** Wartość z konta petenta (imię i nazwisko z mObywatela). */
  prefill?: "firstName" | "lastName";
  /** Wartość początkowa (kod opcji, true/false albo tekst). */
  defaultValue?: string | boolean | string[];
  /** „select”/„radio”/„checkboxes”. */
  options?: IEbiFieldOption[];
  /**
   * „repeat”: pola jednej pozycji i nazwa pozycji („Działka”). Pola pozycji mogą mieć `visibleWhen`/`requiredWhen` wskazujące
   * pola najwyższego poziomu (np. kolumna listy tylko dla jednego wariantu); lista nieobowiązkowa startuje bez pozycji.
   */
  fields?: IEbiField[];
  itemLabel?: string;
  /** „fee”: stawka w zł, pole z liczbą sztuk (np. egzemplarzy) i warunek zwolnienia. */
  feeAmount?: number;
  feeUnitField?: string;
  feeWaivedWhen?: EbiCondition;
}

export interface IEbiFormSection {
  code: string;
  title: string;
  description?: string;
  visibleWhen?: EbiCondition;
  fields: IEbiField[];
}

export interface IEbiFormAttachment {
  code: string;
  label: string;
  hint?: string;
  required?: boolean;
  requiredWhen?: EbiCondition;
  visibleWhen?: EbiCondition;
  /** Wiele plików w jednej pozycji (np. „Inne załączniki”). */
  multiple?: boolean;
}

export enum EEbiFormCategory {
  General = "general",
  Planning = "planning",
  Property = "property",
  Taxes = "taxes",
  Environment = "environment",
  Housing = "housing",
  Roads = "roads",
  Permits = "permits",
  Community = "community",
  Social = "social",
  Education = "education",
  Safety = "safety",
  Vehicles = "vehicles",
  Construction = "construction",
  Geodesy = "geodesy",
  Disability = "disability",
  Labour = "labour",
}

/**
 * Kto załatwia sprawę (części katalogu docs/magistrat/ebi-wnioski) — urząd włącza w ustawieniach EBI wnioski, które
 * przyjmuje; domyślnie gminne (urząd gminy i jego jednostki).
 */
export enum EEbiFormOffice {
  /** Urząd gminy (A, B). */
  Municipality = "municipality",
  /** Jednostki organizacyjne gminy: OPS, GZD, oświata, ZBiLK, straż gminna (C, D, E1–E7). */
  MunicipalUnit = "municipalUnit",
  /** Starostwo powiatowe (F, G). */
  County = "county",
  /** Jednostki organizacyjne powiatu: PCPR, PUP, PZD, PINB (H, E8–E12). */
  CountyUnit = "countyUnit",
}

/** Rodzaj głównego dokumentu przesyłki w biurze podawczym (systemowe rodzaje załączników: ParcelAttachmentTypes). */
export type EbiDocumentType = "Wniosek" | "Deklaracja" | "Pismo" | "Skarga" | "Zapytanie";

export interface IEbiFormVariant {
  /** Tytuł przesyłki w biurze podawczym dla tego wariantu. */
  title: string;
  documentType?: EbiDocumentType;
}

/** Sprawa załatwiana poza EBI (monopol innego systemu albo innego organu) — katalog pokazuje, gdzie i jak ją złożyć. */
export interface IEbiExternalService {
  /** Nazwa systemu albo organu, np. „praca.gov.pl”. */
  service: string;
  url: string;
  /** Co petent ma zrobić i co przygotować (kilka zdań). */
  text: string;
}

export interface IEbiFormDefinition {
  /** Kod z katalogu BE (EbiFormCatalog) — także atrybut `Kod` w XML. */
  code: string;
  version: number;
  title: string;
  category: EEbiFormCategory;
  office: EEbiFormOffice;
  /** Rodzaj głównego dokumentu przesyłki (domyślnie „Wniosek”). */
  documentType?: EbiDocumentType;
  /** Krótki opis na karcie katalogu. */
  description: string;
  /** Słowa dla wyszukiwarki katalogu. */
  keywords?: string[];
  organ: string;
  legalBasis: string;
  /** Treść żądania drukowana pod tytułem wizualizacji („Wnoszę o…”). */
  request?: string;
  /** Opłata — pełny opis w rozwinięciu karty katalogu i w nagłówku formularza. */
  fee: string;
  /** Opłata w skrócie na karcie katalogu (do 16 znaków, np. „17 zł”, „bez opłaty”, „wg stawek”). */
  feeLabel?: string;
  /** Termin załatwienia sprawy. */
  deadline?: string;
  /** Termin w skrócie na karcie katalogu i w metryce wniosku (do 18 znaków, np. „do miesiąca”); jest, gdy jest `deadline`. */
  deadlineLabel?: string;
  /** Pole, którego wartość jest wariantem wniosku w BE (np. rodzaj pisma: skarga/wniosek/petycja). */
  variantField?: string;
  /** Warianty (klucz = wartość opcji pola `variantField`, wszystkie opcje) — z nich powstaje katalog BE (EbiFormCatalog). */
  variants?: Record<string, IEbiFormVariant>;
  /** Sprawa poza EBI: bez sekcji i załączników, katalog odsyła do właściwego systemu (nie ma jej w katalogu BE). */
  external?: IEbiExternalService;
  sections: IEbiFormSection[];
  attachments: IEbiFormAttachment[];
}

/** Pliki dołączone przez petenta — klucz: kod załącznika z definicji. */
export type EbiAttachmentFiles = Record<string, File[]>;

/** Kod błędu walidacji (tłumaczony w komponencie: ebi.submission.errors.*). */
export type EbiFieldError =
  | "required"
  | "pesel"
  /** poprawny PESEL, ale nie osoby zalogowanej mObywatelem (sprawdza BE) */
  | "peselMismatch"
  | "nip"
  | "email"
  | "phone"
  | "postalCode"
  | "number"
  | "date"
  | "maxLength"
  | "minItems"
  | "fileRequired";

/** Błędy: klucz pola (`kod`, w listach `lista.indeks.kod`) albo załącznika (`att:kod`). */
export type EbiFormErrors = Record<string, EbiFieldError>;
